import { ReactElement, useEffect, useRef } from 'react';

import { AirportGroupDropdown } from '@/components/AirportDropdown';
import Button from '@/common/Button';
import ConfirmationButton from '@/components/ConfirmationButton';
import FormButton from '@/components/FormButton';
import Input from '@/common/Input';
import QueryFilters from '@/components/QueryFilters';
import { STATUSES } from '@/constants';
import Select from '@/common/Select';
import VehicleSettingsModal from '@/features/Vehicles/components/VehicleSettingsModal';
import { VehiclesTypes } from '@/features/Vehicles/types';
import deleteVehicleBulk from '@/api/services/vehicles/deleteVehicleBulk';
import { handleError } from '@/utils';
import { useVehiclesState } from '@/features/Vehicles';

const VehicleFilters = (): ReactElement => {
  const searchRef = useRef<HTMLInputElement>(null);

  const [{ airportCode, active, fetch, selected, onReset, search, vehicleSettings, toggleModal, rememberFilters }, setState] =
    useVehiclesState(({ state, setState }) => [
      {
        airportCode: state.filters.airportCode,
        active: state.filters.active,
        fetch: state.fetch,
        selected: state.selected,
        onReset: state.onReset,
        search: state.table.search,
        vehicleSettings: state.modals.vehicleSettings,
        toggleModal:
          (name: keyof VehiclesTypes.Modals, show?: boolean): (() => void) =>
          (): void => {
            setState((current) => ({
              ...current,
              modals: {
                ...current.modals,
                [name]: { ...current.modals[name], show: show ?? !current.modals[name]?.show },
              },
            }));
          },
        rememberFilters: state.settings.rememberFilters,
      },
      setState,
    ]);

  const handleFilterChange =
    (key: keyof VehiclesTypes.Filters) =>
    (value: string | string[]): void => {
      setState((current) => ({
        ...current,
        filters: {
          ...current.filters,
          [key]: value,
        },
      }));
    };

  const handleSearchChange = (value: string | number = ''): void => {
    setState((current) => ({ ...current, table: { ...current.table, search: `${value}` } }));
  };

  const handleDelete = async (): Promise<void> => {
    try {
      await deleteVehicleBulk(selected);
      await fetch();
      setState((current) => ({ ...current, selected: [] }));
    } catch (err) {
      handleError(err);
    }
  };

  const handleOpenCreate = (): void => {
    setState((current) => ({
      ...current,
      modals: { ...current.modals, createVehicle: { show: true } },
    }));
  };

  const handleOpenEdit = (): void => {
    setState((current) => ({
      ...current,
      modals: { ...current.modals, editVehicle: { show: true } },
    }));
  };

  useEffect(() => {
    (rememberFilters ? fetch : onReset)();
    /* ONLY ON MOUNT
      Ignore other dependencies so this only fires on mount.
    */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <QueryFilters className="VehicleFilters justify-content-between">
        <div className="d-flex gap-2 align-items-center">
          <QueryFilters.Input>
            <AirportGroupDropdown
              value={airportCode}
              onChange={handleFilterChange('airportCode')}
              options={{
                showClearButton: false,
                locale: { 'Select...': 'Airport' },
              }}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <Select name="active" value={active} onChange={handleFilterChange('active')} placeholder="Status">
              {STATUSES.default.map(({ id, displayName }) => (
                <Select.Option key={id} value={`${id}`}>
                  {displayName}
                </Select.Option>
              ))}
            </Select>
          </QueryFilters.Input>
          <QueryFilters.Input>
            <Input
              name="search"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search"
              ref={searchRef}
              style={{ maxWidth: 260 }}
            />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <Button name="SUBMIT_SEARCH" variant="success" className="border-white" onClick={fetch}>
              GO
            </Button>
            <Button name="RESET_SEARCH" variant="outline-light" onClick={onReset}>
              Reset
            </Button>
          </QueryFilters.Control>
        </div>
        <div className="d-flex gap-2">
          <QueryFilters.Control>
            <FormButton
              icon={<i className="sv sv-plus-square {font-size:1.5rem;}" />}
              name="CREATE_VEHICLE"
              variant="outline-light"
              onClick={handleOpenCreate}
            >
              Add Vehicle
            </FormButton>
          </QueryFilters.Control>
          <QueryFilters.Control>
            <FormButton
              icon={<i className="sv sv-layers {font-size:1.5rem;}" />}
              name="EDIT_VEHICLE"
              variant="outline-light"
              onClick={handleOpenEdit}
              disabled={selected.length !== 1}
            >
              Edit
            </FormButton>
          </QueryFilters.Control>
          <QueryFilters.Control>
            <ConfirmationButton
              name="DELETE_VEHICLE"
              variant="outline-light"
              onConfirm={handleDelete}
              disabled={selected.length === 0}
              options={{
                confirmation: {
                  Header: { title: 'Confirm Delete' },
                  Body: {
                    message:
                      selected.length > 1
                        ? `Are you sure you want to delete these ${selected.length} vehicles?`
                        : 'Are you sure you want to delete this vehicle?',
                  },
                  Footer: { resolve: 'Yes', reject: 'Cancel' },
                },
              }}
            >
              <i className="sv sv-trash2" />
            </ConfirmationButton>
          </QueryFilters.Control>
          <QueryFilters.Control>
            <Button variant="outline-light" onClick={toggleModal('vehicleSettings', true)}>
              <i className="sv sv-cog fs-4" />
            </Button>
          </QueryFilters.Control>
        </div>
      </QueryFilters>
      <VehicleSettingsModal show={vehicleSettings?.show} onHide={toggleModal('vehicleSettings', false)} />
    </>
  );
};

export default VehicleFilters;
